import { RiCheckLine, RiCloseCircleLine, RiErrorWarningLine, RiInformationLine } from '@remixicon/react';
import { cn } from '@/shared/lib/cn';
import { renderIcon, type IconLike } from './icon';
import { tv, type VariantProps } from './lib/tv';

/**
 * Badge / status pill — kit 67:10639 (spec §4.4). Product uses Small Tinted (Green, Neutral, Red, Blue) and
 * base-palette tints (Shaxsiy / Ommaviy / "2 foydalanuvchi", §1.8).
 */
export const badgeStyles = tv({
  slots: {
    base: 'inline-flex max-w-full shrink-0 items-center font-semibold whitespace-nowrap',
    label: 'min-w-0 truncate px-2',
    icon: 'size-16 shrink-0',
  },
  variants: {
    size: {
      sm: { base: 'h-20 gap-2 rounded-4 px-4 py-2 text-xs' }, // 600 12/16
      md: { base: 'h-24 gap-2 rounded-4 px-4 py-2 text-sm' }, // 600 14/20
    },
    variant: {
      tinted: '',
      filled: { base: 'text-white-900' },
      ghost: { base: 'h-16 gap-4 bg-transparent px-0 py-0', label: 'px-0' },
    },
    color: {
      neutral: '',
      accent: '',
      red: '',
      green: '',
      peach: '',
      teal: '',
      yellow: '',
      coral: '',
      pink: '',
      blue: '',
      violet: '',
      'base-yellow': '',
      'base-green': '',
      'base-blue': '',
      'base-orange': '',
      'base-red': '',
    },
  },
  compoundVariants: [
    // tinted: colour @20 % + colour text (neutral: White/5 + White/60)
    { variant: 'tinted', color: 'neutral', class: { base: 'bg-subtle text-fg-secondary' } },
    { variant: 'tinted', color: 'accent', class: { base: 'bg-tone-cyan-20 text-tone-cyan' } },
    { variant: 'tinted', color: 'red', class: { base: 'bg-tone-red-20 text-tone-red' } },
    { variant: 'tinted', color: 'green', class: { base: 'bg-tone-green-20 text-tone-green' } },
    { variant: 'tinted', color: 'peach', class: { base: 'bg-tone-peach-20 text-tone-peach' } },
    { variant: 'tinted', color: 'teal', class: { base: 'bg-tone-teal-20 text-tone-teal' } },
    { variant: 'tinted', color: 'yellow', class: { base: 'bg-tone-lime-20 text-tone-lime' } },
    { variant: 'tinted', color: 'coral', class: { base: 'bg-tone-coral-20 text-tone-coral' } },
    { variant: 'tinted', color: 'pink', class: { base: 'bg-tone-pink-20 text-tone-pink' } },
    { variant: 'tinted', color: 'blue', class: { base: 'bg-tone-blue-20 text-tone-blue' } },
    { variant: 'tinted', color: 'violet', class: { base: 'bg-tone-violet-20 text-tone-violet' } },
    { variant: 'tinted', color: 'base-yellow', class: { base: 'bg-yellow-200 text-yellow-1000' } },
    { variant: 'tinted', color: 'base-green', class: { base: 'bg-green-200 text-green-1000' } },
    { variant: 'tinted', color: 'base-blue', class: { base: 'bg-blue-200 text-blue-1000' } },
    { variant: 'tinted', color: 'base-orange', class: { base: 'bg-orange-200 text-orange-1000' } },
    { variant: 'tinted', color: 'base-red', class: { base: 'bg-dark-red-200 text-dark-red-1000' } },
    // filled: solid colour + White/90 text (neutral: Secondary BG)
    { variant: 'filled', color: 'neutral', class: { base: 'bg-secondary-bg-1000' } },
    { variant: 'filled', color: 'accent', class: { base: 'bg-tone-cyan' } },
    { variant: 'filled', color: 'red', class: { base: 'bg-tone-red' } },
    { variant: 'filled', color: 'green', class: { base: 'bg-tone-green' } },
    { variant: 'filled', color: 'peach', class: { base: 'bg-tone-peach' } },
    { variant: 'filled', color: 'teal', class: { base: 'bg-tone-teal' } },
    { variant: 'filled', color: 'yellow', class: { base: 'bg-tone-lime' } },
    { variant: 'filled', color: 'coral', class: { base: 'bg-tone-coral' } },
    { variant: 'filled', color: 'pink', class: { base: 'bg-tone-pink' } },
    { variant: 'filled', color: 'blue', class: { base: 'bg-tone-blue' } },
    { variant: 'filled', color: 'violet', class: { base: 'bg-tone-violet' } },
    { variant: 'filled', color: 'base-yellow', class: { base: 'bg-yellow-1000' } },
    { variant: 'filled', color: 'base-green', class: { base: 'bg-green-1000' } },
    { variant: 'filled', color: 'base-blue', class: { base: 'bg-blue-1000' } },
    { variant: 'filled', color: 'base-orange', class: { base: 'bg-orange-1000' } },
    { variant: 'filled', color: 'base-red', class: { base: 'bg-dark-red-1000' } },
    // ghost: coloured text only
    { variant: 'ghost', color: 'neutral', class: { base: 'text-fg-secondary' } },
    { variant: 'ghost', color: 'accent', class: { base: 'text-tone-cyan' } },
    { variant: 'ghost', color: 'red', class: { base: 'text-tone-red' } },
    { variant: 'ghost', color: 'green', class: { base: 'text-tone-green' } },
    { variant: 'ghost', color: 'peach', class: { base: 'text-tone-peach' } },
    { variant: 'ghost', color: 'teal', class: { base: 'text-tone-teal' } },
    { variant: 'ghost', color: 'yellow', class: { base: 'text-tone-lime' } },
    { variant: 'ghost', color: 'coral', class: { base: 'text-tone-coral' } },
    { variant: 'ghost', color: 'pink', class: { base: 'text-tone-pink' } },
    { variant: 'ghost', color: 'blue', class: { base: 'text-tone-blue' } },
    { variant: 'ghost', color: 'violet', class: { base: 'text-tone-violet' } },
    { variant: 'ghost', color: 'base-yellow', class: { base: 'text-yellow-1000' } },
    { variant: 'ghost', color: 'base-green', class: { base: 'text-green-1000' } },
    { variant: 'ghost', color: 'base-blue', class: { base: 'text-blue-1000' } },
    { variant: 'ghost', color: 'base-orange', class: { base: 'text-orange-1000' } },
    { variant: 'ghost', color: 'base-red', class: { base: 'text-dark-red-1000' } },
  ],
  defaultVariants: { size: 'sm', variant: 'tinted', color: 'neutral' },
});

export type BadgeColor = NonNullable<VariantProps<typeof badgeStyles>['color']>;

export interface BadgeProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'color'>, VariantProps<typeof badgeStyles> {
  children?: React.ReactNode;
  leftIcon?: IconLike;
  rightIcon?: IconLike;
}

export function Badge({ size, variant, color, leftIcon, rightIcon, className, children, ...rest }: BadgeProps) {
  const s = badgeStyles({ size, variant, color });
  return (
    <span {...rest} className={s.base({ class: className })}>
      {renderIcon(leftIcon, s.icon())}
      {children != null && <span className={s.label()}>{children}</span>}
      {renderIcon(rightIcon, s.icon())}
    </span>
  );
}

/** Semantic statuses (spec §1.8). */
export type Status = 'ok' | 'warning' | 'danger' | 'info' | 'neutral';

const STATUS_COLOR: Record<Status, BadgeColor> = {
  ok: 'green',
  warning: 'yellow',
  danger: 'red',
  info: 'blue',
  neutral: 'neutral',
};
const STATUS_ICON: Record<Status, IconLike | undefined> = {
  ok: RiCheckLine,
  warning: RiErrorWarningLine,
  danger: RiCloseCircleLine,
  info: RiInformationLine,
  neutral: undefined,
};

export interface StatusPillProps extends Omit<BadgeProps, 'color' | 'variant'> {
  status: Status;
  /** Leading glyph; `true` uses the status default (check / warning / close-circle / info), `false` hides it. */
  icon?: IconLike | boolean;
}

/** Product status pill: Badge sm Tinted coloured by status ("Kiradi", "Kirmaydi", "Normal", "Tahrirlanmoqda"…). */
export function StatusPill({ status, icon = false, size = 'sm', ...rest }: StatusPillProps) {
  const leftIcon = icon === true ? STATUS_ICON[status] : icon === false ? undefined : icon;
  return <Badge {...rest} size={size} variant="tinted" color={STATUS_COLOR[status]} leftIcon={leftIcon} />;
}

/** Live sensor statuses (02-constructor §2.10). */
export type LiveStatus = 'normal' | 'warning' | 'danger' | 'offline' | 'none';

const DOT_COLOR: Record<LiveStatus, string> = {
  normal: 'bg-other-03e42d',
  warning: 'bg-other-f0c429',
  danger: 'bg-dark-red-1000',
  offline: 'bg-other-99a3b8',
  none: 'bg-fg-tertiary',
};
const HALO: Record<LiveStatus, string> = {
  normal: 'shadow-[0_0_0_0.25rem_var(--color-other-03e42d-10)]',
  warning: 'shadow-[0_0_0_0.25rem_var(--color-other-f0c429-10)]',
  danger: 'shadow-[0_0_0_0.25rem_var(--color-dark-red-100)]',
  offline: 'shadow-[0_0_0_0.25rem_#99a3b81a]',
  none: '',
};

export interface StatusDotProps {
  status: LiveStatus;
  /** Diameter in design px. Default 8 (chart legends, list rows). */
  size?: 6 | 8 | 10 | 12 | 14;
  /** 10 % halo ring around the dot. */
  halo?: boolean;
  /** Accessible status text (otherwise decorative). */
  label?: string;
  className?: string;
}

const DOT_SIZE = { 6: 'size-6', 8: 'size-8', 10: 'size-10', 12: 'size-12', 14: 'size-14' } as const;

export function StatusDot({ status, size = 8, halo, label, className }: StatusDotProps) {
  return (
    <span
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className={cn('inline-block shrink-0 rounded-full', DOT_SIZE[size], DOT_COLOR[status], halo && HALO[status], className)}
    />
  );
}
